import { LanguageFlag } from "@@/icons/flags/flag";
import { browserLogger } from "@@/settings";
import { useLanguageContext } from "@@/settings/language-context";
import { useTranslate } from "@@/translations/use-translate";
import { useTheme } from "@emotion/react";
import type { LanguageCode } from "@towni/common";
import {
    assertNever,
    languageCodes,
    languageNameCube,
    languageNameNative,
    sortAlphabetically,
} from "@towni/common";
import { useEffect, useMemo, useState } from "react";
import type { Except } from "type-fest";
import { ButtonTransparent } from "./buttons_v2/button-gray";
import { Select } from "./components/select";
import { ContextMenu } from "./context-menu/context-menu";
import { HorizontalDivider } from "./dividers";
import { FlexColumn, FlexRow } from "./flex-containers";
import { ForEach } from "./for-each";
import { usePageBreakpoint } from "./responsiveness/use-page-breakpoint";
import { FieldTitle } from "./text/field-title";

type Props = {
    className?: string;
    /**
     * When given, these will be the available languages instead of the default ones.
     * By default the component will first use the languages given in this prop.
     * If not given it will use the languages given in the `useLanguageContext` hook (the available languages set in context).
     * If available languages in context are not set, it will use all languages that has been defined in the system.
     */
    languages?: LanguageCode[] | readonly LanguageCode[];
    override?: {
        language?: LanguageCode;
        setLanguage?: (language: LanguageCode) => void;
    };
    /** Render picker even when there's only one option */
    alwaysShow?: boolean;
    /** When true language names will be shown in their respective language instead of the current language */
    showLanguageNameInNative?: boolean;
    /** No label will be rendered above the select */
    hideLabel?: boolean;
    flagOnly?: boolean;
    /**
     * Sizes
     * - normal: react-select dropdown, flag and text
     * - mini: flag only
     * - mini-floating: flag only, drop shadow
     */
    type?: "normal" | "mini" | "mini-floating";
};

const LanguagePicker = (props: Props) => {
    switch (props.type) {
        case "mini":
        case "mini-floating":
            return <_LanguagePicker_Mini {...props} type={props.type} />;
        case undefined:
        case "normal":
            return <_LanguagePicker_Normal {...props} type={props.type} />;
        default:
            assertNever(props.type);
    }
};

const _LanguagePicker_Menu_Item = (props: {
    option: { value: LanguageCode; label: string; icon: React.JSX.Element };
    onSelect: (language: LanguageCode) => void;
}) => {
    const theme = useTheme();
    const { value, label, icon } = props.option;
    return (
        <ButtonTransparent
            onClick={() => props.onSelect(value)}
            css={{
                flex: 1,
                borderRadius: 0,
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                "&:hover": {
                    backgroundColor: theme.colors.primary.light.asString,
                },
            }}>
            <FlexRow
                mainAxis="flex-start"
                crossAxis="center"
                css={{ padding: 5, paddingRight: 20 }}>
                {icon}
                <HorizontalDivider />
                {label}
            </FlexRow>
        </ButtonTransparent>
    );
};
const _LanguagePicker_Menu = (props: {
    onSelect: (language: LanguageCode) => void;
    options: { value: LanguageCode; label: string; icon: React.JSX.Element }[];
}) => {
    return (
        <FlexColumn crossAxis="stretch">
            <ForEach itemOf={props.options}>
                {({ value, label, icon }) => (
                    <_LanguagePicker_Menu_Item
                        option={{ value, label, icon }}
                        onSelect={props.onSelect}
                    />
                )}
            </ForEach>
        </FlexColumn>
    );
};

const _LanguagePicker_Mini = (
    props: Except<Props, "type"> & {
        type: "mini" | "mini-floating";
    },
) => {
    const [isOpen, setIsOpen] = useState(false);
    const translate = useTranslate();
    const languageContext = useLanguageContext();
    const currentContextLanguage = languageContext.language;
    const currentLanguage = props.override?.language || currentContextLanguage;
    const availableLanguages =
        props.languages || languageContext.availableLanguages || languageCodes;
    const setLanguage =
        props.override?.setLanguage || languageContext.setLanguage;

    const options = useMemo(() => {
        return availableLanguages
            .map(lang => {
                return {
                    value: lang,
                    label: props.showLanguageNameInNative
                        ? languageNameNative[lang]
                        : languageNameCube[lang][currentContextLanguage],
                    icon: <LanguageFlag language={lang} size={1.2} />,
                };
            })
            .sort(sortAlphabetically(item => translate(item.label)));
    }, [
        availableLanguages,
        currentContextLanguage,
        props.showLanguageNameInNative,
        translate,
    ]);

    useEffect(() => {
        if (availableLanguages.length === 1) {
            browserLogger.info(
                `Only one language available (${availableLanguages[0]}), switching to that language`,
            );
            if (currentLanguage !== availableLanguages[0]) {
                setLanguage(availableLanguages[0]);
            }
        }
    }, [availableLanguages, currentLanguage, setLanguage]);

    // If there is no choice, should we render?
    if (availableLanguages.length <= 1 && !props.alwaysShow) return null;

    return (
        <ContextMenu
            key={currentContextLanguage}
            isOpen={isOpen}
            menuContainerCss={{
                borderRadius: 5,
                padding: 0,
                paddingTop: 5,
                paddingBottom: 5,
                overflow: "hidden",
            }}
            allowedPlacements={[
                "bottom",
                "bottom-start",
                "bottom-end",
                "top",
                "top-start",
                "top-end",
            ]}
            setIsOpen={setIsOpen}
            menu={menuProps => (
                <_LanguagePicker_Menu
                    onSelect={language => {
                        setLanguage(language);
                        menuProps.actions.close();
                        setIsOpen(false);
                    }}
                    options={options}
                />
            )}>
            <FlexRow>
                {props.hideLabel ? null : (
                    <>
                        <FieldTitle
                            text={{
                                sv: "Språk:",
                                en: "Language:",
                                de: "Sprache:",
                            }}
                        />
                        <HorizontalDivider XS />
                    </>
                )}
                <ButtonTransparent
                    css={{ padding: 5 }}
                    onClick={() => setIsOpen(!isOpen)}>
                    <FlexRow
                        mainAxis="flex-start"
                        crossAxis="center"
                        css={
                            props.type === "mini-floating"
                                ? {
                                      willChange: "filter",
                                      filter: "drop-shadow(2px 2px 15px rgba(0,0,0,0.7))",
                                  }
                                : undefined
                        }>
                        <LanguageFlag
                            css={{ borderRadius: 2 }}
                            language={currentLanguage}
                            size={1.5}
                        />
                    </FlexRow>
                </ButtonTransparent>
            </FlexRow>
        </ContextMenu>
    );
};

const _LanguagePicker_Normal = ({
    type: _size,
    ...props
}: Except<Props, "type"> & { type?: "normal" }) => {
    const translate = useTranslate();
    const languageContext = useLanguageContext();
    const currentContextLanguage = languageContext.language;
    const currentLanguage = props.override?.language || currentContextLanguage;
    const availableLanguages =
        props.languages || languageContext.availableLanguages || languageCodes;
    const setLanguage =
        props.override?.setLanguage || languageContext.setLanguage;

    const options = useMemo(() => {
        return availableLanguages
            .map(language => ({
                value: language,
                label: props.flagOnly
                    ? ""
                    : props.showLanguageNameInNative
                      ? languageNameNative[language]
                      : languageNameCube[language][currentContextLanguage],
                icon: <LanguageFlag language={language} size={1.2} />,
            }))
            .sort(sortAlphabetically(item => translate(item.label)));
    }, [
        availableLanguages,
        currentContextLanguage,
        props.showLanguageNameInNative,
        props.flagOnly,
        translate,
    ]);

    // If there is no choice, should we render?
    if (availableLanguages.length <= 1 && !props.alwaysShow) return null;

    return (
        <Select
            options={options}
            value={currentLanguage}
            onValueChange={value => {
                if (value) {
                    setLanguage(value);
                }
            }}
        />
    );
};

const LanguagePickerBackOfficeToolbarItem = (props: Except<Props, "type">) => {
    // const theme = useTheme();
    const isDesktop = usePageBreakpoint({ when: "🖥" });

    switch (isDesktop) {
        case true:
            return (
                <LanguagePicker
                    type="mini"
                    hideLabel
                    {...props}
                    showLanguageNameInNative
                />
            );
        default:
            return (
                <FlexRow fillParentWidth css={{ padding: 20 }}>
                    <LanguagePicker
                        type="normal"
                        hideLabel
                        {...props}
                        showLanguageNameInNative
                    />
                </FlexRow>
            );
    }
};

export { LanguagePicker, LanguagePickerBackOfficeToolbarItem };
export type { Props as LanguagePickerProps };
